import { extend } from "../../typeinfo/jquery/index";
import { Button } from "../core/components/button";
import { Dialog } from "../core/components/dialog";
import { IsSearchField, IsSearchFieldProps } from "../core/components/isSearchField";
import { KendoHtmlEditorCommercial } from "../core/components/kendoHtmlEditorCommercial";
import { ComponentHelper } from "../core/util/componentHelper";
import { KendoChart } from "../statistics/statistic_components/chart_component";
import React from "react";
type KendoFieldType = "string" | "number";
//type ColumnWidthType = `${number}${"px" | "%"}`

export interface KendoColumn<T> {
    title: string
    template?: (dataItem: T) => string
    editable?: (dataItem: T) => boolean
    editor?: (container: JQuery<HTMLElement>, dataItem: T) => void
    width?: string
}

interface TreeListProps<T> {
    data: T[]
    idField: { fieldName: keyof T, fieldType: KendoFieldType }
    parentId: { fieldName: keyof T, fieldType: KendoFieldType }
    sortField: { fieldName: keyof T, fieldType: KendoFieldType }
    columns: KendoColumn<T>[]
    onSelectionChanged: (selectedItems: T[]) => void
    onKeyDown?: (ctrlKey: boolean, shiftKey: boolean, altKey: boolean, key: string, dataItem: T) => void
}

export class TreeList<T extends object> extends React.Component<TreeListProps<T>, any>
{
    private templateCache = {};

    appendItem(newItem: any) {
        this._treeList.dataSource.pushInsert(this._treeList.dataSource.total(), newItem);
    }

    getItemAt(position: number) {
        return this._treeList.dataSource.at(position);
    }

    private _treeListRoot: HTMLDivElement;
    private _treeList: kendo.ui.TreeList;

    _handleSelectionChanged = (dataItem) => {
        if (!this.props.onSelectionChanged) {
            return;
        }

        const selectedRows = this._treeList.select();
        const selectedDataItems: T[] = [];
        for (var i = 0; i < selectedRows.length; i++) {
            const dI: any = this._treeList.dataItem(selectedRows[i]);
            selectedDataItems.push(dI);
        }

        this.props.onSelectionChanged(selectedDataItems);
    }

    private addToCache = (dataItem: T, columnIndex: number, htmlString: string) => {
        const cachingKey = "" + dataItem["id"] + "///" + columnIndex;
        this.templateCache[cachingKey] = htmlString;
    }

    private getFromCache = (dataItem: T, columnIndex: number): string => {
        const cachingKey = "" + dataItem["id"] + "///" + columnIndex;
        return this.templateCache[cachingKey];
    }

    private templateWrapper = (dataItem: T, columnIndex: number, templateFunction: (dataItem: T) => string) => {
        const componentHtml = templateFunction(dataItem);
        return `<div className="templateRoot" style="pointer-events:none;display:inline"}}>${componentHtml}</div>`;
    }

    private mapColumnsToKendoColumns = (columns: KendoColumn<T>[]): any[] => {
        if (!columns) {
            return [];
        }

        const kendoColumns = columns.map((c, i) => {
            const column: kendo.ui.TreeListColumn = {
                field: "__dummy__" + i, title: c.title || " ",
                template: (dataIem) => this.templateWrapper(dataIem, i, c.template),
                editable: c.editor ? c.editable : () => false,
                editor: (container, props: any) => { c.editor(container, props.model) },
                width: c.width ?? "150px"
            }
            return column;
        });

        return kendoColumns;
    }

    componentDidMount() {

        const fields: any = {};
        fields[this.props.idField.fieldName] = { type: this.props.idField.fieldType };
        fields[this.props.parentId.fieldName] = { type: this.props.parentId.fieldType, nullable: true };
        fields[this.props.sortField.fieldName] = { type: this.props.sortField.fieldType, nullable: true };

        const dataSource = new kendo.data.TreeListDataSource({
            data: this.props.data,
            schema: {
                model: {
                    id: this.props.idField.fieldName,
                    parentId: this.props.parentId.fieldName,
                    fields: fields
                }
            },
            sort: [
                { field: this.props.sortField.fieldName, dir: "asc" }
            ]

        });
        const config: kendo.ui.TreeListOptions = {
            dataSource: dataSource,
            editable: { mode: "inCell" },
            navigatable: true,
            selectable: "multiple, row",
            change: this._handleSelectionChanged,
            columns: this.mapColumnsToKendoColumns(this.props.columns)
        };

        this._treeList = $(this._treeListRoot).kendoTreeList(config).data("kendoTreeList");
        if (this.props.onKeyDown) {
            this.registerKeyDownHandler();
        }
    }

    private registerKeyDownHandler = () => {
        $(this._treeListRoot).keydown((e) => {
            const focusedCell = $(this._treeListRoot).find(".k-state-focused");
            let focusedDataItem: any = null;
            if (focusedCell) {
                const focusedRow = focusedCell.parent();
                focusedDataItem = this._treeList.dataItem(focusedRow);
            }
            this.props.onKeyDown && this.props.onKeyDown(e.ctrlKey, e.shiftKey, e.altKey, e.key, focusedDataItem);
        })
    }

    componentDidUpdate(prevProps: TreeListProps<T>, prevState: any) {
        if (this.props.data !== prevProps.data) {
            this.kendoFastRedrawGrid();
        }
    }

    private kendoFastRedrawGrid = () => {
        for (var i = 0; i < this._treeList.items().length; i++) {
            const item = this._treeList.items()[i];
            this.kendoFastRedrawRow(this._treeList, item);
        }
    }

    private kendoFastRedrawRow = (grid, row) => {
        const dataItem = grid.dataItem(row);
        const rowChildren = $(row).children('td[role="gridcell"]');

        for (let i = 0; i < grid.columns.length; i++) {
            this._drawCell(grid, i, rowChildren, dataItem);
        }
    }

    private _drawCell(grid: any, i: number, rowChildren: JQuery<any>, dataItem: any) {
        var column = grid.columns[i];
        var template = column.template;
        var cell = rowChildren[i].children[rowChildren[i].children.length - 1];
        if (cell && template !== undefined && rowChildren[i].className != "k-edit-cell") {
            // Render using template
            const newHtml = template(dataItem, template);
            if (this.getFromCache(dataItem, i) != newHtml) {
                cell.innerHTML = newHtml;
                this.addToCache(dataItem, i, newHtml);
            }
        }
    }

    render() {
        return <div className="flex-container">
            <div className="flex-container flex-container__item" ref={(r) => this._treeListRoot = r}></div>
        </div>
    }
}

export function dropDownEditorHelper(container, dataSource: any[], value, textField: string, valueField: string, allowNull: boolean, onChange: (newvalue: any) => void) {
    const settings: kendo.ui.DropDownListOptions = {
        valuePrimitive: true, //N.B. this is needed to have correct behavior when the initial value can be null
        autoBind: true,
        value: value,
        dataTextField: textField,
        dataValueField: valueField,
        dataSource: {
            data: dataSource
        },
        change: (e) => {
            onChange && onChange(e.sender.dataItem() ? e.sender.dataItem().Value : null);
        }
    };
    if (allowNull) {
        settings.optionLabel = " ";
    }
    $('<input />')
        .appendTo(container)
        .kendoDropDownList(settings);
}

export function textEditorHelper(container, value: string, onChange: (newvalue: string) => void) {
    $('<input />')
        .val(value)
        .addClass('k-textbox')
        .appendTo(container)
        .blur((e) => {
            const input = e.target as HTMLInputElement;
            const newValue = input.value;
            if (value !== newValue) {
                onChange && onChange(newValue);
            }
        })
}

export function numericEditorHelper(container, value: number, onChange: (newvalue: number) => void) {
    $('<input />')
        .val(value)
        .appendTo(container)
        .blur((e) => {
            const input = e.target as HTMLInputElement;
            const newValue = +input.value;
            if (value !== newValue) {
                onChange && onChange(newValue);
            }
        })
        .kendoNumericTextBox({})
}

export function booleanEditorHelper(container: Element, value: boolean, onChange: (newvalue: boolean) => void) {
    $('<input type="checkbox" class="k-checkbox k-valid"/>')
        .prop('checked', value)
        .change((e) => {
            const input = e.target as HTMLInputElement;
            onChange(input.checked);
        })
        .appendTo(container);
}

export function getSearch(value: any, onChange: (newvalue: any) => void) {
    const searchProps = getSearchParamsByType();

    //const datarowId = cell.parent().attr('data-uid');
    //const cellId = cell.attr('aria-describedby');

    return <IsSearchField {...searchProps}
        onOpened={() => { }}
        handleIsSearchFieldFullObjectModel={
            (q, newValue) => {
                onChange && onChange(newValue);
            }}
        onChange={(q, newValue, e) => {
            onChange && onChange(newValue);
        }}
        value={value}
        onClose={() => {
            //const newCell = this.findNewCell(datarowId, cellId);
            //this.grid.editCell(newCell)
        }}
        preventButtonFocus={true}
    />
}


function getSearchParamsByType(): IsSearchFieldProps {
    const componentHelper = ComponentHelper.createFactory();
    const componentSearchProps: IsSearchFieldProps = componentHelper.getDefaultSearchFieldProps();
    componentSearchProps.multiSelectMode = false;
    componentSearchProps.field.searchWindowUrl = "DynamicSearch/Window/Parts?fullObject=true";
    componentSearchProps.field.dataTextField = "Artikelnummer";
    componentSearchProps.field.dataValueField = "Artikelnummer";
    componentSearchProps.field.searchValueField = "Artikelnummer";
    componentSearchProps.field.searchTextField = "Artikelnummer";
    componentSearchProps.field.autocompleteEnabled = true;
    componentSearchProps.field.autocompleteDataTextField = "Artikelnummer";
    componentSearchProps.field.autocompleteUrl = "ArticleLookup/Find?fullObject=true";
    componentSearchProps.field.autocompleteTemplate = "<table cellspacing='0' style='table-layout:fixed; width:650px'><colgroup><col style='width: 100px' /><col style='width: 250px' /><col style='width: 300px' /></colgroup><tr><td style='vertical-align:top; word-wrap: break-word;'>${data.Artikelnummer}</td><td style='vertical-align:top'>${data.Bezeichnung}</td></tr></table>";;
    return componentSearchProps;
}

export function getMemoEditor(value: any, onChange: (newvalue: any) => void) {
    return <MemoEditor value={value} />;
}

class MemoEditor extends React.Component<{ value: string }, { showEditor: boolean }> {

    constructor(props) {
        super(props)
        this.state = { showEditor: false };
    }

    render() {
        const dialog = this.state.showEditor ? < Dialog open={true} title="Title" actions={[{ label: "DoIt", primary: true, action: () => { } }]} >

        </Dialog> : null;
        return <>
            {dialog}
            <div>Memo:{this.props.value}<button onClick={() => { this.setState({ showEditor: true }) }}>...</button></div>
        </>;
    }
}